import { InfoCircled } from "@/assets/icons";
import {
  Button,
  ButtonVariant,
  Divider,
  Row,
  RowSize,
  SideDrawer,
} from "@/components";
import {
  selectPersonelsData,
  selectRequestFormStore,
  useBoundStore,
} from "@/services/stores";
import clsx from "clsx";
import { find, get, map, size } from "lodash";
import { FC, Fragment, useState } from "react";
import { useSort } from "../../hooks/use-sort";
import { useUsersOps } from "../../hooks/use-user-ops";
import styles from "../../styles.scss";
import { UsersField } from "../../types";
import { prepeareUserDate } from "../../utils";
import { ModalContent } from "./modal-content";
import { TableHeader } from "./table-header";
import { TableRow } from "./table-row";
import { Person, Personnels } from "@/services";
import { useFormContext } from "react-hook-form";

export const UsersA: FC<{
  isInbox?: boolean;
  inboxData?: Person[];
  onSelectUser?: (value: Personnels, dropdownId: UsersField.Users) => void;
}> = ({ isInbox, onSelectUser, inboxData = null }) => {
  const { personnels } = useBoundStore(selectPersonelsData);
  const { requestFormData } = useBoundStore(selectRequestFormStore);
  const errors = useFormContext()?.formState?.errors;

  const [data, updateData] = useState<Person[] | null>(personnels || inboxData);

  const {
    filteredData,
    selectedOptions,
    sortDirections,
    searchValue,
    appliedFilters,
    handleApplyFilter,
    handleSort,
    handleSearch,
    handleFilterChange,
  } = useSort(data, updateData);

  const {
    isSelectedAll,
    isOpenModalUser,
    onToggleSelectUser,
    onToggleAll,
    onToggleUserModal,
    onUpdatePersonnel,
  } = useUsersOps({ data: filteredData, onSelectUser });

  const isPartialSelected =
    get(requestFormData, UsersField.Users, "").length > 0 &&
    get(requestFormData, UsersField.Users, "").length < size(data);

  const renderTableHeader = () => {
    return (
      <TableHeader
        data={data}
        appliedFilters={appliedFilters}
        searchValue={searchValue}
        selectedOptions={selectedOptions}
        sortDirections={sortDirections}
        isSelectedAll={isSelectedAll}
        isPartialSelected={isPartialSelected}
        isInbox={isInbox}
        onToggleAll={onToggleAll}
        onSortBy={handleSort}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilter}
      />
    );
  };

  const renderTable = () => {
    return (
      <div
        className={styles.table}
        style={{ minWidth: isInbox ? "auto" : "900px" }}
      >
        {data && filteredData ? (
          map(filteredData, (item, index) => {
            const user = prepeareUserDate(item);

            const isCheckedUser = find(requestFormData?.Personnels, {
              WorkerEidUnifiedNumber: user.WorkerEidUnifiedNumber,
            });

            if (user.WorkerEidUnifiedNumber !== "1") {
              return (
                <Fragment key={index}>
                  <TableRow
                    item={user}
                    isInbox={isInbox}
                    isCheckedUser={isCheckedUser}
                    onToggleSelectUser={onToggleSelectUser}
                    onToggleUserModal={onToggleUserModal}
                  />
                </Fragment>
              );
            }
          })
        ) : (
          <Row className={styles.emptyRow}>No data available</Row>
        )}
      </div>
    );
  };

  return (
    <>
      {!isInbox && (
        <>
          <div className={styles.noticeInfo}>
            <InfoCircled width={40} height={40} />

            <div
              style={{
                fontSize: "16px",
                lineHeight: "24px",
              }}
            >
              <div style={{ fontWeight: "bold" }}>
                Can&apos;t Find Personnel?
              </div>
              <div style={{ color: "#999" }}>
                If you&apos;re unable to locate the personnel you&apos;re
                searching for, follow the link to update their details.
              </div>
            </div>

            <Button
              variant={ButtonVariant.Transparent}
              className={styles.updatePersonnelBtn}
              rel="noreferrer"
              onClick={onUpdatePersonnel}
            >
              Update Personnel
            </Button>
          </div>

          {renderTableHeader()}
        </>
      )}

      <div className={clsx(styles.tableWrap, isInbox && styles.tableWrapInbox)}>
        {isInbox ? (
          <>
            {renderTableHeader()}

            {renderTable()}
          </>
        ) : (
          renderTable()
        )}
      </div>

      <Row size={RowSize.ExtraLarge} className={styles.isRed}>
        {get(errors, [UsersField.Users, "message"]) as string | undefined}
      </Row>

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>

      <SideDrawer
        title="Personnel Details"
        isOpen={!!isOpenModalUser}
        onClose={() => onToggleUserModal(null)}
      >
        <ModalContent
          onClose={() => onToggleUserModal(null)}
          user={isOpenModalUser}
        />
      </SideDrawer>
    </>
  );
};
